<template>
  <div class="sb-landing-version">
    <LandingPageOriginal v-if="showOriginal" />
    <LandingPageV2 v-else-if="showVersionTwo" />
    <v-app v-else>
      <v-main class="landingpage">
        <article
          class="sb-public-landing"
          :class="{ 'sb-public-landing--dark': $vuetify.theme.dark }"
          lang="en"
          aria-labelledby="sb-landing-title"
        >
          <section class="sb-hero">
            <video
              ref="heroVideo"
              class="sb-hero-video"
              muted
              autoplay
              loop
              playsinline
              preload="metadata"
              aria-hidden="true"
            >
              <source
                :src="require('@/assets/images/pages/landingpage/globe.mp4')"
                type="video/mp4"
              />
            </video>
            <div class="sb-hero-shade"></div>
            <div class="sb-landing-container sb-hero-content">
              <div class="sb-hero-brand">
                <img
                  :src="require('@/assets/images/logos/selfbio-logo-6.png')"
                  alt=""
                  width="56"
                  height="56"
                />
                <span><span>SELF</span>BIOGRAPHY</span>
              </div>
              <h1 id="sb-landing-title">
                {{ $t("\n                Record Your Life.") }}<br />{{ $t("Tell Your Story.") }}<br />{{ $t("Leave Your Mark on the World.\n              ") }}
              </h1>
              <p class="sb-hero-intro">
                {{ $t("\n                Everyone has a story worth preserving for themselves, the people they love, and generations still to come. Build your biography one memory at a time.\n              ") }}
              </p>
              <p class="sb-hero-puzzle">
                {{ $t("\n                Every memory is a piece. Your life is the puzzle.\n              ") }}
              </p>
              <div class="sb-hero-actions">
                <router-link
                  class="sb-landing-button sb-landing-button--primary"
                  :to="joinTarget"
                >
                  {{ $t("\n                  Join free\n                ") }}
                </router-link>
                <router-link
                  class="sb-landing-button sb-landing-button--hero"
                  to="/pages/pricing#choose-plan"
                >
                  {{ $t("\n                  Subscribe\n                ") }}
                </router-link>
                <router-link
                  class="sb-landing-button sb-landing-button--hero"
                  to="/pages/affiliate-program"
                >
                  {{ $t("\n                  Earn\n                ") }}
                </router-link>
              </div>
              <p class="sb-hero-note">
                {{ $t("\n                Discover Self Biography and start your Life Puzzle. Subscriptions from ") }}{{ startingPrice }}{{ $t("/month.\r\n              ") }}
              </p>
            </div>
          </section>

          <section
            class="sb-landing-section sb-devices-section"
            aria-labelledby="sb-devices-title"
          >
            <div class="sb-landing-container sb-devices-layout">
              <div class="sb-devices-box">
                <p class="sb-eyebrow">
                  {{ $t("\n                  Across devices\n                ") }}
                </p>
                <h2 id="sb-devices-title">
                  {{ $t("\n                  Anytime, anywhere.\n                ") }}
                </h2>
                <p>{{ $t("Your life, your memories, and your life’s work, all in one place, organized chronologically and just one click away on any device.") }}</p>
                <ul
                  class="sb-device-list"
                  :aria-label="$t(&quot;Devices and platforms&quot;)"
                >
                  <li
                    v-for="device in content.devices"
                    :key="device"
                  >
                    {{ device }}
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-benefits-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                Your story, your way\n              ") }}
              </p>
              <h2 id="sb-benefits-title">
                {{ $t("\n                A place for every piece of your life.\n              ") }}
              </h2>
              <div class="sb-benefits-grid">
                <section
                  v-for="(benefit, index) in content.benefits"
                  :key="benefit.title"
                  class="sb-benefit"
                >
                  <span
                    class="sb-section-number"
                    aria-hidden="true"
                  >0{{ index + 1 }}</span>
                  <h3>{{ benefit.title }}</h3>
                  <p>{{ benefit.text }}</p>
                </section>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section"
            aria-labelledby="sb-discover-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                More than memories\n              ") }}
              </p>
              <h2 id="sb-discover-title">
                {{ $t("\n                The person. The work. The life behind both.\n              ") }}
              </h2>
            </div>
            <div
              class="sb-story-carousel"
              role="region"
              aria-roledescription="carousel"
              :aria-label="$t(&quot;More than memories&quot;)"
              @mouseenter="carouselHovered = true"
              @mouseleave="carouselHovered = false"
              @focusin="carouselFocused = true"
              @focusout="onCarouselFocusOut"
            >
              <div :aria-live="carouselPaused ? 'polite' : 'off'">
                <section
                  v-for="(story, index) in content.stories"
                  v-show="activeStory === index"
                  :key="story.id"
                  class="sb-carousel-slide"
                  role="group"
                  aria-roledescription="slide"
                  :aria-label="(index + 1) + ' of ' + content.stories.length"
                  :style="{ backgroundImage: 'url(' + require('@/assets/images/pages/landingpage/' + story.image) + ')' }"
                >
                  <div class="sb-landing-container sb-carousel-copy">
                    <h3>{{ story.title }}</h3>
                    <p
                      v-for="paragraph in story.paragraphs"
                      :key="paragraph"
                    >
                      {{ paragraph }}
                    </p>
                    <details
                      v-if="story.more"
                      class="sb-reading-more"
                    >
                      <summary>{{ story.more.title }}</summary>
                      <p
                        v-for="paragraph in story.more.paragraphs"
                        :key="paragraph"
                      >
                        {{ paragraph }}
                      </p>
                    </details>
                    <router-link
                      v-if="story.link"
                      class="sb-text-link"
                      :to="story.link.to"
                    >
                      {{ story.link.label }} →
                    </router-link>
                  </div>
                </section>
              </div>
              <div class="sb-carousel-controls">
                <button
                  type="button"
                  :aria-label="$t(&quot;Previous story&quot;)"
                  @click="moveStory(-1)"
                >
                  ‹
                </button>
                <div
                  class="sb-carousel-dots"
                  :aria-label="$t(&quot;Choose a story&quot;)"
                >
                  <button
                    v-for="(story, index) in content.stories"
                    :key="story.id"
                    type="button"
                    :aria-label="story.title"
                    :aria-current="activeStory === index ? 'true' : null"
                    @click="activeStory = index"
                  >
                    <span></span>
                  </button>
                </div>
                <button
                  type="button"
                  :aria-label="$t(&quot;Next story&quot;)"
                  @click="moveStory(1)"
                >
                  ›
                </button>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-features-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                Current features\n              ") }}
              </p>
              <h2 id="sb-features-title">
                {{ $t("\n                Start preserving your story today.\n              ") }}
              </h2>
              <p class="sb-section-intro">
                {{ $t("\n                Bring the people, places, photographs, accomplishments, and everyday moments of your life together.\n              ") }}
              </p>
              <ol class="sb-feature-timeline">
                <li
                  v-for="feature in content.features"
                  :key="feature.title"
                  class="sb-timeline-item"
                >
                  <span
                    class="sb-timeline-dot"
                    aria-hidden="true"
                  ><svg
                    viewBox="0 0 24 24"
                    focusable="false"
                  ><path d="M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21 12 17.76 5.82 21 7 14.14 2 9.27 8.91 8.26Z" /></svg></span>
                  <section class="sb-timeline-card">
                    <h3>{{ feature.title }}</h3>
                    <p>{{ feature.text }}</p>
                  </section>
                </li>
              </ol>
              <router-link
                class="sb-text-link"
                to="/pages/documentation"
              >
                {{ $t("\n                Explore the documentation →\n              ") }}
              </router-link>
            </div>
          </section>

          <section
            id="life-puzzle"
            class="sb-landing-section"
            aria-labelledby="sb-puzzle-title"
          >
            <div class="sb-landing-container sb-puzzle-layout">
              <div
                class="sb-puzzle-picture"
                aria-hidden="true"
              >
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/2.webp')"
                    alt=""
                    loading="lazy"
                  /><span>{{ $t("Memories") }}</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/10.webp')"
                    alt=""
                    loading="lazy"
                  /><span>{{ $t("Life’s work") }}</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/1.webp')"
                    alt=""
                    loading="lazy"
                  /><span>{{ $t("Connections") }}</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/15.webp')"
                    alt=""
                    loading="lazy"
                  /><span>{{ $t("Experiences") }}</span>
                </div>
              </div>
              <div>
                <p class="sb-eyebrow">
                  {{ $t("\n                  The Life Puzzle\n                ") }}
                </p>
                <h2 id="sb-puzzle-title">
                  {{ $t("\n                  Every memory is a piece.") }}<br />{{ $t("Your life is the puzzle.\n                ") }}
                </h2>
                <p>{{ $t("Start anywhere: in childhood, yesterday, your travels, or your career. Connect photographs, dates, people, and places. Ask others about missing pieces and watch your biography take shape.") }}</p>
                <p class="sb-puzzle-statement">
                  {{ $t("\n                  There is no predetermined picture on the box. The picture is you.\n                ") }}
                </p>
                <details class="sb-reading-more sb-game-details">
                  <summary>{{ $t("Discover the game") }}</summary>
                  <p class="sb-status-label">
                    {{ $t("\n                    Game experience in development\n                  ") }}
                  </p>
                  <h3>{{ $t("The Life Puzzle Game") }}</h3>
                  <p>{{ $t("Turn memories into Memory Bits, the individual tiles of your Life Puzzle. Each tile can hold photographs, stories, videos, dates, places, people, and shared experiences. The proposed game experience builds on these pieces to reveal the bigger picture of your life.") }}</p>
                  <h3>{{ $t("Easy game onboarding") }}</h3>
                  <p>{{ $t("Start anywhere. Begin with one memory or a collection. Build backward, forward, or anywhere in between. The vision is to help you match people, places, dates, and historical events, discover missing pieces, and invite others to help complete your puzzle.") }}</p>
                  <h3>{{ $t("Rewards & SB Tokens") }}</h3>
                  <p>{{ $t("Proposed internal game tokens would reward completing Memory Bits and making meaningful connections. These rewards are a future concept, separate from purchased AI credits and real-money affiliate commissions.") }}</p>
                </details>
              </div>
            </div>
          </section>

          <section
            id="plans-earning"
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-plans-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                Plans & earning\n              ") }}
              </p>
              <h2 id="sb-plans-title">
                {{ $t("\n                Your subscription builds more than your biography.\n              ") }}
              </h2>
              <div class="sb-plans-intro">
                <p>{{ $t("Self Biography was created with a different idea about how a social platform can support itself. We want the people who benefit from Self Biography to help make it possible.") }}</p>
                <p>{{ $t("Starting at ") }}{{ startingPrice }}{{ $t(" a month, small contributions from a growing community help support storage, security, AI tools, development, and long-term preservation.") }}</p>
                <p><strong>{{ $t("A little from each of us can build something substantial for all of us.") }}</strong></p>
              </div>
              <div
                v-if="catalog"
                class="sb-plans-grid"
              >
                <section
                  v-for="plan in catalog.plans"
                  :key="plan.id"
                  class="sb-plan"
                  :class="{ 'sb-plan--personal': plan.id === 'personal' }"
                >
                  <h3>{{ plan.name }}</h3>
                  <p class="sb-plan-price">
                    {{ money(plan.amount, plan.currency) }}<span v-if="plan.amount">{{ $t(" / month") }}</span>
                  </p>
                  <p>{{ planDescriptions[plan.id] }}</p>
                  <ul>
                    <li>{{ storage(plan.storageBytes) }}{{ $t(" storage") }}</li>
                    <li v-if="plan.creditPeriod === 'welcome'">
                      {{ plan.credits }}{{ $t(" welcome AI credits, valid for 30 days\n                    ") }}
                    </li>
                    <li v-else>
                      {{ plan.credits.toLocaleString(this.$locale()) }}{{ $t(" AI credits each billing cycle\n                    ") }}
                    </li>
                  </ul>
                  <router-link
                    class="sb-landing-button"
                    :class="{ 'sb-landing-button--primary': plan.id === 'personal' }"
                    :to="plan.id === 'free' ? joinTarget : '/pages/pricing#choose-plan'"
                  >
                    {{ plan.id === 'free' ? $t("Join free") : $t("Explore plan") }}
                  </router-link>
                </section>
              </div>
              <p v-else-if="pricingUnavailable">
                {{ $t("\n                Visit Plans & Subscriptions for the current prices and allowances.\n              ") }}
              </p>
              <p
                v-else
                role="status"
              >
                {{ $t("\n                Loading current plans…\n              ") }}
              </p>
              <p class="sb-plan-note">
                {{ $t("\n                All plans include Personal and Professional biographies and privacy controls. Paid plans add capacity and AI credits. Monthly credits and purchased credits remain separate; purchased packs do not expire. Existing subscriptions keep their current prices and allowances until you choose to change plans.\n              ") }}
              </p>
              <router-link
                class="sb-text-link"
                to="/pages/pricing"
              >
                {{ $t("\n                Compare Plans & Subscriptions →\n              ") }}
              </router-link>
              <div class="sb-earning-intro">
                <div>
                  <h3>{{ $t("Help grow the community and participate in its success.") }}</h3>
                  <p>{{ $t("Your subscription helps support Self Biography. The Affiliate Program gives you an opportunity to participate in its growth. Introduce Self Biography to family, friends, professional networks, or your audience. Qualifying purchases may earn you a 25% real-money commission under the program terms.") }}</p>
                </div>
                <router-link
                  class="sb-landing-button sb-landing-button--primary"
                  to="/pages/affiliate-program"
                >
                  {{ $t("\n                  Explore the Affiliate Program\n                ") }}
                </router-link>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section"
            aria-labelledby="sb-coming-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                Coming soon & future development\n              ") }}
              </p>
              <h2 id="sb-coming-title">
                {{ $t("\n                More chapters are still to come.\n              ") }}
              </h2>
              <p class="sb-section-intro">
                {{ $t("\n                The following ideas are in development or part of our future vision. They are separate from the features and subscription benefits available today.\n              ") }}
              </p>
              <ol class="sb-feature-timeline sb-timeline--planned">
                <li
                  v-for="feature in content.planned"
                  :key="feature.title"
                  class="sb-timeline-item"
                >
                  <span
                    class="sb-timeline-dot"
                    aria-hidden="true"
                  ><svg
                    viewBox="0 0 24 24"
                    focusable="false"
                  ><path d="M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21 12 17.76 5.82 21 7 14.14 2 9.27 8.91 8.26Z" /></svg></span>
                  <section class="sb-timeline-card">
                    <h3>{{ feature.title }}</h3>
                    <p>{{ feature.text }}</p>
                  </section>
                </li>
              </ol>
              <div class="sb-future-copy">
                <h3>{{ $t("Beyond the screen") }}</h3>
                <p>{{ $t("Social technology should bring people closer together. As Self Biography evolves, we envision tools for family reunions, gatherings with friends, school reunions, professional meetings, and community events, turning online connections into real-world experiences and preserving them as new Memory Bits.") }}</p>
                <p><strong>{{ $t("Connect online. Come together in life. Make new memories.") }}</strong></p>
                <details class="sb-reading-more">
                  <summary>{{ $t("Building a community based on trust") }}</summary>
                  <p>{{ $t("Today, Self Biography is about preserving lives, memories, relationships, and accomplishments. As the community grows, that foundation may also make commerce, creative collaboration, professional connections, and personal relationships possible.") }}</p>
                  <p>{{ $t("A voluntarily shared biography can provide history and context while each member controls what remains private. Marketplace, commerce, and dating remain future concepts; biography history alone does not verify identity or guarantee trustworthiness.") }}</p>
                </details>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section sb-closing"
            aria-labelledby="sb-start-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                {{ $t("\n                Your story is worth preserving\n              ") }}
              </p>
              <h2 id="sb-start-title">
                {{ $t("\n                Start anywhere.") }}<br />{{ $t("Start with one memory.\n              ") }}
              </h2>
              <p>{{ $t("Bring people together through their shared memories rather than keep them apart by their opinions.") }}</p>
              <router-link
                class="sb-landing-button sb-landing-button--primary"
                :to="joinTarget"
              >
                {{ $t("\n                Join free\n              ") }}
              </router-link>
            </div>
          </section>
        </article>
      </v-main>
    </v-app>
  </div>
</template>

<script>
import { t, localizeCopy } from '@/i18n'

// Keep the backup untouched, including its existing legacy CSS for shared layouts.
import LandingPageOriginal from './LandingPageOriginal.vue'
import LandingPageV2 from './LandingPageV2.vue'

import content from '@/content/landingPageContentV3.json'
import billing from '@/services/billing'

export default {
  name: 'PublicLandingPage',
  components: { LandingPageOriginal, LandingPageV2 },
  data: () => ({
    activeStory: 0,
    carouselPaused: false,
    carouselHovered: false,
    carouselFocused: false,
    carouselTimer: null,
    catalog: null,
    pricingUnavailable: false,
    planDescriptions: {
      free: 'Discover Self Biography and begin preserving your story.',
      personal: 'Preserve your memories, photographs, diary, and family history.',
      professional: 'Preserve your life and your life’s work: career, projects, and portfolio.',
      business_plus: 'More space and AI credits for professional history and a growing archive.',
    },
  }),
  computed: {
    content() { return localizeCopy(content) },
    showOriginal() { return ['original', '1'].includes(this.$route.query.version) },
    showVersionTwo() { return this.$route.query.version === '2' },
    showCurrent() { return !this.showOriginal && !this.showVersionTwo },
    joinTarget() { return this.$store.state.logged ? '/my-biography' : '/auth' },
    startingPrice() {
      const personal = this.catalog && this.catalog.plans.find(plan => plan.id === 'personal')

      return personal ? this.money(personal.amount, personal.currency) : '$2'
    },
  },
  watch: {
    showCurrent(value) {
      if (value) this.prepareLanding()
      else clearInterval(this.carouselTimer)
    },
  },
  mounted() { if (this.showCurrent) this.prepareLanding() },
  beforeDestroy() { clearInterval(this.carouselTimer) },
  methods: {
    prepareLanding() {
      clearInterval(this.carouselTimer)
      this.carouselPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.carouselTimer = setInterval(() => {
        if (!this.carouselPaused && !this.carouselHovered && !this.carouselFocused && !document.hidden) this.moveStory(1)
      }, 9000)
      if (!this.catalog) {
        billing.catalog().then(catalog => { this.catalog = catalog; this.pricingUnavailable = false })
          .catch(() => { this.pricingUnavailable = true })
      }
      this.$nextTick(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && this.$refs.heroVideo) {
          this.$refs.heroVideo.pause()
        }
      })
    },
    money(amount, currency = 'usd') {
      return new Intl.NumberFormat(this.$locale(), {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
      }).format(amount / 100)
    },
    storage(bytes) { return bytes < 1000000000 ? t('{{value0}} MB', { value0: bytes / 1000000 }) : t('{{value0}} GB', { value0: bytes / 1000000000 }) },
    moveStory(direction) {
      this.activeStory = (this.activeStory + direction + this.content.stories.length) % this.content.stories.length
    },
    onCarouselFocusOut(event) {
      this.carouselFocused = event.currentTarget.contains(event.relatedTarget)
    },
  },
}
</script>

<style scoped lang="scss">
.sb-landing-version { min-width: 0; }
.sb-public-landing {
  --sb-text: #5e5669; --sb-heading: #393345; --sb-accent: #713ac7; --sb-surface: #fff; --sb-tint: #f7f3fc; --sb-line: #e6dfef;
  color: var(--sb-text); background: var(--sb-surface); font-size: 16px; line-height: 1.75; text-align: left;
  &--dark { --sb-text: #d5cfe2; --sb-heading: #f0ebf8; --sb-accent: #c6a5ff; --sb-surface: #28243d; --sb-tint: #312d4b; --sb-line: #514662; }
  h1, h2, h3 { text-align: left; word-break: normal; letter-spacing: -.025em; }
  h2 { color: var(--sb-heading); font-size: clamp(1.8rem, 3vw, 2.8rem); line-height: 1.22; font-weight: 500; margin: 0 0 24px; }
  h3 { color: var(--sb-heading); font-size: 1.2rem; line-height: 1.4; font-weight: 600; margin: 0 0 16px; }
  p { margin: 0 0 20px; }
  img { margin: 0; max-width: 100%; }
  a, button, summary { -webkit-tap-highlight-color: transparent; }
  a:focus-visible, button:focus-visible, summary:focus-visible, [role=tabpanel]:focus-visible { outline: 2px solid var(--sb-accent); outline-offset: 4px; }
}
.sb-landing-container { width: min(1180px, calc(100% - 64px)); margin: 0 auto; }
.sb-landing-section { padding: 88px 0; }
.sb-landing-section--tint { background: var(--sb-tint); }
.sb-public-landing .sb-eyebrow { color: var(--sb-accent); font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; margin-bottom: 16px; }
.sb-section-intro { max-width: 720px; margin-bottom: 32px !important; }
.sb-hero { position: relative; display: flex; align-items: center; min-height: 730px; overflow: hidden; background: #0c0b19; color: #fff; }
.sb-public-landing .sb-hero-video { position: absolute; inset: 0; width: 100%; height: 100%; min-height: 100%; max-height: none; object-fit: cover; }
.sb-hero-shade { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(5,4,16,.91) 0%, rgba(5,4,16,.75) 48%, rgba(5,4,16,.18) 100%); }
.sb-hero-content { position: relative; z-index: 1; padding: 64px 0 84px; }
.sb-hero-brand { display: flex; align-items: center; gap: 16px; margin-bottom: 28px; }
.sb-public-landing .sb-hero-brand img { width: 56px; height: 56px; filter: brightness(0) invert(1); }
.sb-hero-brand > span { font-family: copperplatelight, sans-serif; font-size: 24px; letter-spacing: 1px; }
.sb-hero-brand > span > span { color: #b98dff; }
.sb-hero h1 { color: #fff; font-size: clamp(2.4rem, 4.3vw, 4.3rem); line-height: 1.12; font-weight: 500; max-width: 820px; margin-bottom: 26px; }
.sb-public-landing .sb-hero-intro { font-size: 19px; line-height: 1.65; max-width: 650px; color: #e9e3f3; }
.sb-hero-puzzle { font-weight: 600; color: #fff; }
.sb-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; margin: 28px 0 16px; }
.sb-public-landing .sb-landing-button { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; padding: 12px 22px; border: 1px solid var(--sb-line); border-radius: 5px; color: var(--sb-accent); font-size: 12px; font-weight: 700; letter-spacing: .07em; line-height: 1.4; text-align: center; text-decoration: none; text-transform: uppercase; transition: background-color .15s ease; }
.sb-public-landing .sb-landing-button:hover { background: var(--sb-tint); }
.sb-public-landing .sb-landing-button--primary { background: #713ac7; border-color: #713ac7; color: #fff; }
.sb-public-landing .sb-landing-button--primary:hover { background: #6030aa; }
.sb-public-landing .sb-landing-button--hero { border-color: rgba(255,255,255,.55); color: #fff; }
.sb-public-landing .sb-landing-button--hero:hover { background: rgba(255,255,255,.13); }
.sb-public-landing .sb-hero-note { font-size: 13px; color: #d8d1e5; margin: 0; max-width: 650px; }
.sb-device-list { list-style: none; padding: 0; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 24px; }
.sb-device-list li { font-size: 12px; padding: 3px 10px; border: 1px solid var(--sb-line); border-radius: 20px; }
.sb-benefits-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 32px; margin-top: 40px; }
.sb-benefit { padding-top: 20px; border-top: 1px solid var(--sb-line); }
.sb-section-number { display: block; font-size: 14px; color: var(--sb-accent); margin-bottom: 20px; }
.sb-benefit p { font-size: 15px; margin: 0; }
.sb-public-landing .sb-text-link { color: var(--sb-accent); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; min-height: 44px; margin-top: 12px; }
.sb-text-link:hover { text-decoration: underline; text-underline-offset: 4px; }
.sb-reading-more { border-top: 1px solid var(--sb-line); margin-top: 24px; padding-top: 12px; }
.sb-reading-more summary { cursor: pointer; color: var(--sb-accent); font-weight: 600; padding: 10px 0; }
.sb-reading-more[open] summary { margin-bottom: 12px; }
.sb-puzzle-layout { display: grid; grid-template-columns: 1fr 1.2fr; align-items: start; gap: 64px; }
.sb-puzzle-picture { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 10px; background: var(--sb-tint); border-radius: 12px; transform: rotate(-3deg); margin-top: 36px; }
.sb-puzzle-piece { position: relative; overflow: hidden; border-radius: 6px; min-width: 0; }
.sb-public-landing .sb-puzzle-piece img { display: block; width: 100%; height: 170px; object-fit: cover; }
.sb-puzzle-piece span { position: absolute; left: 0; right: 0; bottom: 0; padding: 24px 12px 8px; color: #fff; background: linear-gradient(transparent,rgba(0,0,0,.8)); font-size: 12px; }
.sb-puzzle-statement { font-weight: 600; color: var(--sb-heading); }
.sb-game-details h3 { margin-top: 24px; }
.sb-public-landing .sb-status-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--sb-accent); }
.sb-plans-intro { max-width: 850px; }
.sb-plans-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 16px; margin: 40px 0 24px; }
.sb-plan { display: flex; flex-direction: column; padding: 24px 20px; border: 1px solid var(--sb-line); border-radius: 7px; background: var(--sb-surface); }
.sb-plan--personal { border-color: var(--sb-accent); }
.sb-public-landing .sb-plan-price { font-size: 34px; line-height: 1.4; font-weight: 500; color: var(--sb-heading); margin: 0 0 20px; }
.sb-plan-price span { font-size: 12px; font-weight: 400; color: var(--sb-text); white-space: nowrap; }
.sb-plan > p:not(.sb-plan-price), .sb-plan li { font-size: 14px; }
.sb-plan ul { padding-left: 18px; margin-bottom: 24px; }
.sb-plan .sb-landing-button { margin-top: auto; }
.sb-public-landing .sb-plan-note { font-size: 13px; max-width: 930px; }
.sb-earning-intro { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 32px; border-top: 1px solid var(--sb-line); margin-top: 36px; padding-top: 36px; }
.sb-earning-intro p { max-width: 760px; margin-bottom: 0; }
.sb-future-copy { max-width: 850px; margin-top: 40px; }
.sb-closing { background: var(--sb-tint); }
.sb-closing p { max-width: 680px; }
@media(max-width:959px) {
  .sb-landing-section { padding: 64px 0; }
  .sb-puzzle-layout { grid-template-columns: 1fr; gap: 32px; }

  .sb-benefits-grid { gap: 20px; }
  .sb-benefit h3 { font-size: 1.1rem; }
  .sb-plans-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .sb-puzzle-picture { max-width: 550px; margin: 0 12px; }
  .sb-earning-intro { grid-template-columns: 1fr; gap: 20px; }
  .sb-earning-intro .sb-landing-button { justify-self: start; }
}
@media(max-width:599px) {
  .sb-landing-container { width: calc(100% - 40px); }
  .sb-landing-section { padding: 48px 0; }
  .sb-hero { min-height: 0; }
  .sb-hero-content { padding: 40px 0 68px; }
  .sb-hero-brand { gap: 12px; margin-bottom: 24px; }
  .sb-hero-brand > span { font-size: 20px; }
  .sb-public-landing .sb-hero-brand img { width: 42px; height: 42px; }
  .sb-hero h1 { font-size: clamp(2rem, 8.9vw, 2.7rem); line-height: 1.16; }
  .sb-public-landing .sb-hero-intro { font-size: 16px; }
  .sb-hero-actions { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 8px; }
  .sb-hero-actions .sb-landing-button { padding: 12px 8px; font-size: 11px; }
  .sb-benefits-grid { grid-template-columns: 1fr; }
  .sb-benefits-grid { margin-top: 20px; gap: 24px; }
  .sb-benefit p, .sb-feature p { font-size: 15px; }

  .sb-plans-grid { grid-template-columns: 1fr; margin-top: 28px; }
  .sb-plan { padding: 24px; }
  .sb-public-landing .sb-puzzle-piece img { height: 135px; }
  .sb-earning-intro .sb-landing-button { justify-self: stretch; }
}
@media(prefers-reduced-motion:reduce) {
  .sb-public-landing *, .sb-public-landing *::before, .sb-public-landing *::after { transition: none !important; scroll-behavior: auto !important; }
}

.sb-devices-section { background: #f5f4fa url('~@/assets/images/pages/landing-laptop.webp') no-repeat right center / auto 130%; padding: 72px 0; }
.sb-devices-layout { min-height: 460px; display: flex; align-items: center; }
.sb-devices-box { width: 530px; max-width: 100%; padding: 40px; background: rgba(255,255,255,.76); border: 1px solid var(--sb-line); border-radius: 12px; box-shadow: 0 16px 48px rgba(40,30,65,.12); backdrop-filter: blur(2px); }
.sb-public-landing--dark .sb-devices-box { background: rgba(40,36,61,.8); }
.sb-story-carousel { position: relative; margin-top: 36px; background: #211a32; color: #fff; }
.sb-carousel-slide { position: relative; min-height: 640px; display: flex; align-items: center; padding: 64px 0 100px; background-size: cover; background-position: center; isolation: isolate; animation: sb-story-appear .6s ease; }
@keyframes sb-story-appear { from { opacity: 0; } to { opacity: 1; } }
@media(prefers-reduced-motion:reduce) { .sb-carousel-slide { animation: none; } }
.sb-carousel-slide::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg,rgba(36,20,60,.93),rgba(36,20,60,.76),rgba(20,12,30,.58)); z-index: -1; }
.sb-carousel-copy { padding-right: 15%; }
.sb-public-landing .sb-carousel-copy h3 { color: #fff; font-size: clamp(1.8rem,3vw,2.6rem); }
.sb-carousel-copy p { font-size: 19px; line-height: 1.8; text-shadow: 0 1px 3px rgba(0,0,0,.4); }
.sb-public-landing .sb-carousel-copy .sb-text-link, .sb-carousel-copy summary { color: #e0c9ff; }
.sb-carousel-copy .sb-reading-more { border-color: rgba(255,255,255,.35); }
.sb-carousel-controls { position: absolute; z-index: 2; left: 0; right: 0; bottom: 16px; display: flex; align-items: center; justify-content: center; gap: 12px; padding: 0 20px; }
.sb-carousel-controls button { min-width: 44px; min-height: 44px; border-radius: 5px; color: #fff; font-size: 32px; }
.sb-carousel-controls button:hover { background: rgba(255,255,255,.12); }
.sb-carousel-dots { display: flex; }
.sb-carousel-dots button { min-width: 32px; display: grid; place-items: center; }
.sb-carousel-dots span { width: 8px; height: 8px; border-radius: 50%; background: #a39ab5; }
.sb-carousel-dots [aria-current=true] span { background: #fff; box-shadow: 0 0 0 4px rgba(255,255,255,.15); }
.sb-feature-timeline { position: relative; list-style: none; padding: 0; margin: 48px 0 24px; }
.sb-feature-timeline::before { content: ''; position: absolute; left: 50%; top: 0; bottom: 0; width: 2px; background: var(--sb-line); transform: translateX(-50%); }
.sb-timeline-item { position: relative; display: flex; justify-content: flex-end; padding: 24px 0 0; }
.sb-timeline-item:nth-child(even) { justify-content: flex-start; }
.sb-timeline-dot { position: absolute; left: 50%; top: 24px; transform: translateX(-50%); width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: #9155fd; color: #decaff; box-shadow: 0 2px 4px rgba(0,0,0,.12); }
.sb-timeline-dot svg { display: block; width: 25px; height: 25px; fill: currentColor; }
.sb-timeline-card { position: relative; width: calc(50% - 48px); border-radius: 7px; background: var(--sb-surface); box-shadow: 0 4px 12px rgba(40,30,65,.07); }
.sb-public-landing .sb-timeline-card h3 { position: relative; margin: 0; padding: 16px 20px; border-radius: 7px 7px 0 0; background: #312d4b; color: #eee7fa; font-size: 20px; font-weight: 500; }
.sb-timeline-card h3::before { content: ''; position: absolute; top: 12px; left: -9px; border-top: 10px solid transparent; border-bottom: 10px solid transparent; border-right: 10px solid #312d4b; }
.sb-timeline-item:nth-child(even) h3::before { left: auto; right: -9px; border-right: 0; border-left: 10px solid #312d4b; }
.sb-timeline-card p { margin: 0; padding: 22px 20px; font-size: 15px; }
.sb-public-landing--dark .sb-timeline-card { border: 1px solid var(--sb-line); }
@media(max-width:959px) {
  .sb-devices-section { background-size: auto 115%; background-position: 65% center; padding: 64px 0; }
  .sb-carousel-copy { padding-right: 0; }
}
@media(max-width:599px) {
  .sb-devices-section { padding: 200px 0 40px; background-size: auto 540px; background-position: center top; }
  .sb-devices-layout { min-height: 0; }
  .sb-devices-box { margin-top: 40px; padding: 26px 22px; }
  .sb-carousel-slide { min-height: 790px; padding: 40px 0 100px; }
  .sb-carousel-copy p { font-size: 16px; line-height: 1.75; }
  .sb-carousel-controls { gap: 2px; padding: 0 8px; }
  .sb-carousel-dots button { min-width: 24px; }
  .sb-feature-timeline::before { left: 19px; }
  .sb-timeline-dot { left: 19px; width: 34px; height: 34px; }
  .sb-timeline-dot svg { width: 22px; height: 22px; }
  .sb-timeline-item, .sb-timeline-item:nth-child(even) { justify-content: flex-end; }
  .sb-timeline-card { width: calc(100% - 54px); }
  .sb-public-landing .sb-timeline-card h3 { font-size: 18px; padding: 14px 16px; }
  .sb-timeline-card p { padding: 18px 16px; }
  .sb-timeline-item:nth-child(even) h3::before { left: -9px; right: auto; border-left: 0; border-right: 10px solid #312d4b; }
}
</style>
